export interface CreatorScoreSummary {
  userId: number;
  revision: number;
  formulaVersion: string;
  baseScore: number | null;
  finalScore: number | null;
  reviewStatus:
    | "no_flags_found"
    | "blocked"
    | "review_required"
    | "not_checked";
  freshness: "current" | "stale" | "partial";
  calculatedAt: string;
  inputCutoff: string;
}

export interface CreatorScoreSource {
  name: string;
  observedAt: string;
}

export interface CreatorScoreMetric {
  metric: string;
  rawValue: number | null;
  unit: string;
  score: number | null;
  weight: number | null;
  status: string;
  reason: string;
  evidence: string[];
}

export interface CreatorScoreRiskCase {
  caseId: string;
  kind: "ghosting" | "operational_blacklist";
  campaignId: string | null;
  status: "candidate" | "confirmed" | "exempt" | "disputed";
  reason: string;
  sourceReferences: string[];
}

export interface CreatorScoreSnapshot extends CreatorScoreSummary {
  schemaVersion: 2 | 3 | 4;
  scale: 10;
  runId: string;
  sources: CreatorScoreSource[];
  metrics: CreatorScoreMetric[];
  calculation?: Record<string, unknown>;
  riskCases: CreatorScoreRiskCase[];
  riskRevision: string;
  coverage: number;
}

export interface CampaignCreatorScoresResponse {
  scores: Record<string, CreatorScoreSummary>;
}

export interface CampaignCreatorScoreResponse {
  userId: number | null;
  snapshot: CreatorScoreSnapshot | null;
}

export const SCORE_REVIEW_LABELS = {
  no_flags_found: "No flags found",
  blocked: "Blocked",
  review_required: "Review required",
  not_checked: "Not checked",
};

export function formatCreatorScore(score: number | null | undefined): string {
  return score == null ? "—" : `${score.toFixed(1)} / 10`;
}

/** Shortlisting bands agreed with marketing on 2026-10-27 from posting rates (≥1.5 ≈ 70% posted, <1 ≈ 43%). */
export const MINIMUM_QUALIFYING_SCORE = 1.5;
export const EXCELLENT_SCORE = 2;

export type CreatorScoreTier = "blocked" | "excellent" | "qualifying" | "below_minimum" | "unscored";

export const CREATOR_SCORE_TIER_LABELS: Record<CreatorScoreTier, string> = {
  blocked: "Blocked",
  excellent: "Excellent",
  qualifying: "Qualifies",
  below_minimum: "Below minimum",
  unscored: "No score yet",
};

export function getCreatorScoreTier(
  score: number | null | undefined,
  reviewStatus?: CreatorScoreSummary["reviewStatus"],
): CreatorScoreTier {
  if (reviewStatus === "blocked") {
    return "blocked";
  }
  if (score == null || !Number.isFinite(score)) {
    return "unscored";
  }
  const rounded = Number(score.toFixed(1));
  if (rounded >= EXCELLENT_SCORE) {
    return "excellent";
  }
  if (rounded >= MINIMUM_QUALIFYING_SCORE) {
    return "qualifying";
  }
  return "below_minimum";
}

export function isBelowMinimumScore(score: number | null | undefined): boolean {
  return getCreatorScoreTier(score) === "below_minimum";
}

export function formatCreatorScoreWeight(weight: number | null | undefined): string {
  return weight == null || !Number.isFinite(weight) ? "—" : `${Math.round(weight * 100)}%`;
}

/** Rounds stored measurements for review: whole views and counts, one decimal elsewhere. */
export function formatCreatorScoreMeasurement(rawValue: number | null | undefined, unit: string): string {
  if (rawValue == null || !Number.isFinite(rawValue)) {
    return "—";
  }
  const wholeUnits = new Set(["views", "count", "counts", "posts", "days", "campaigns", "submissions"]);
  const unitWord = unit.trim().split(/\s+/)[0]?.toLowerCase() ?? "";
  const digits = wholeUnits.has(unitWord) || Number.isInteger(rawValue) ? 0 : 1;
  const number = rawValue.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return unit === "score" ? `${number} / 10` : `${number} ${unit}`.trim();
}

export function compareCreatorScores(
  first: number | null | undefined,
  second: number | null | undefined,
  descending: boolean,
): number {
  if (first == null) return second == null ? 0 : 1;
  if (second == null) return -1;
  return descending ? second - first : first - second;
}

const CREATOR_SCORE_METRIC_LABELS: Record<string, string> = {
  content_score: "Content quality",
  internal_score: "Campaign history",
  activity: "Recent activity (30 days)",
  completion: "Completed campaigns",
  timeliness: "On-time posting",
  repeat: "Repeat participation",
  eligible_submissions: "Posts checked for timeliness",
  account_activity: "Account activity",
  contents_quality: "Content topic relevance",
  content_impression: "Content impressions",
  time_to_reapply: "Time to reapply",
  contents_uploading_latency: "Content uploading latency",
  hub_activity: "Hub activity",
  campaign_progress: "Campaign progression",
};

export function getCreatorScoreMetricLabel(metric: string): string {
  return Object.prototype.hasOwnProperty.call(CREATOR_SCORE_METRIC_LABELS, metric)
    ? (CREATOR_SCORE_METRIC_LABELS[metric] ?? metric)
    : metric;
}

const CREATOR_SCORE_METRIC_SUMMARIES: Record<string, string> = {
  content_score: "How good recent posts look, scaled down when they average under 1,000 views.",
  internal_score: "How the creator performed in past SkinSeoul campaigns.",
  activity: "Days with the creator's own posts in the last 30 days. Four days gives full marks.",
  completion: "Campaigns finished after being selected. Four gives full marks.",
  timeliness: "Share of required posts delivered by the deadline.",
  repeat: "Times the creator came back for another campaign. Four gives full marks.",
  eligible_submissions: "How many required posts could be checked for timeliness.",
  account_activity: "How active the connected social account has been.",
  contents_quality: "How closely recent content matches beauty and skincare topics.",
  content_impression: "Typical views on recent posts.",
  time_to_reapply: "How quickly the creator applied again after a campaign.",
  contents_uploading_latency: "How long after delivery the creator posted.",
  hub_activity: "How active the creator is inside Creator Hub.",
  campaign_progress: "How far the creator progressed in past campaigns.",
};

export function getCreatorScoreMetricSummary(metric: string): string | undefined {
  return Object.prototype.hasOwnProperty.call(CREATOR_SCORE_METRIC_SUMMARIES, metric)
    ? CREATOR_SCORE_METRIC_SUMMARIES[metric]
    : undefined;
}

const METRIC_STATUS_LABELS: Record<string, string> = {
  measured: "Measured",
  calculated: "Measured",
  partial: "Partly measured",
  sheet_default: "Default",
  unavailable: "No data",
  not_applicable: "Not applicable",
  policy_pending: "Pending policy",
  review_pending: "Pending review",
  missing: "No data",
};

export function getCreatorScoreMetricStatusLabel(status: string): string {
  return Object.prototype.hasOwnProperty.call(METRIC_STATUS_LABELS, status)
    ? (METRIC_STATUS_LABELS[status] ?? status)
    : status;
}
